/**
 * ContactSettingsTab - Edit contact information displayed on the Contact page
 */
import { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, Clock, Instagram, Facebook, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';
import TikTokIcon from '@/components/icons/TikTokIcon';

export default function ContactSettingsTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [contactInfo, setContactInfo] = useState({
    address: "Shop 3 at Concave Road, Namaka, Nadi, Fiji",
    address_extra: "",
    phone: "+679 675 0102",
    email: "administrator@bulaccinocafe.com",
    hours: "Daily: 6:30 AM - 8:00 PM",
    map_url: "https://maps.google.com/?q=Shop+3+Concave+Road+Namaka+Nadi+Fiji",
    instagram: "https://instagram.com/bulaccinocafe",
    facebook: "https://facebook.com/bulaccinocafe",
    tiktok: "https://www.tiktok.com/@bulaccinocafe"
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/admin/settings');
      const data = res.data;
      setSettings(data);
      if (data.contact_info) {
        setContactInfo(prev => ({ ...prev, ...data.contact_info }));
      }
    } catch (err) {
      toast.error('Failed to load settings');
    }
    setLoading(false);
  };

  const saveContactInfo = async () => {
    setSaving(true);
    try {
      const updatedSettings = {
        ...settings,
        contact_info: contactInfo
      };
      
      await api.put('/admin/settings', updatedSettings);
      toast.success('Contact information saved!');
    } catch (err) {
      toast.error('Failed to save contact info');
    }
    setSaving(false);
  };

  const updateField = (field, value) => {
    setContactInfo(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="contact-settings-tab">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl text-foreground">Contact Information</h1>
          <p className="text-muted-foreground mt-1">
            Edit the contact details shown on the Contact page
          </p>
        </div>
        <Button 
          onClick={saveContactInfo}
          disabled={saving}
          className="rounded-full bg-primary hover:bg-primary/90"
          data-testid="save-contact-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Location & Hours */}
        <div className="bg-card rounded-2xl p-6 shadow-sm border border-border">
          <h2 className="font-heading text-xl mb-6 text-foreground flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary" />
            Location & Hours
          </h2>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-foreground">Address</Label>
              <Input
                value={contactInfo.address}
                onChange={(e) => updateField('address', e.target.value)}
                placeholder="Street address"
                data-testid="contact-address-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-foreground">Additional Address Info (optional)</Label>
              <Input
                value={contactInfo.address_extra}
                onChange={(e) => updateField('address_extra', e.target.value)}
                placeholder="e.g., Across from Fiji Airways Training Center"
                data-testid="contact-address-extra-input"
              />
              <p className="text-xs text-muted-foreground">
                This will appear below the main address
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-foreground">Google Maps Link</Label>
              <Input
                value={contactInfo.map_url}
                onChange={(e) => updateField('map_url', e.target.value)}
                placeholder="https://maps.google.com/..."
                data-testid="contact-map-url-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-foreground flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Opening Hours
              </Label>
              <Textarea
                value={contactInfo.hours}
                onChange={(e) => updateField('hours', e.target.value)}
                placeholder="e.g., Monday-Friday: 7AM-5PM"
                rows={3}
                data-testid="contact-hours-input"
              />
              <p className="text-xs text-muted-foreground">
                You can use multiple lines for different days
              </p>
            </div>
          </div>
        </div>

        {/* Contact Details */}
        <div className="bg-card rounded-2xl p-6 shadow-sm border border-border">
          <h2 className="font-heading text-xl mb-6 text-foreground flex items-center gap-2">
            <Phone className="w-5 h-5 text-primary" />
            Contact Details
          </h2>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-foreground flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Phone Number
              </Label>
              <Input
                value={contactInfo.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                placeholder="+679 XXX XXXX"
                data-testid="contact-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-foreground flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email Address
              </Label>
              <Input
                type="email"
                value={contactInfo.email}
                onChange={(e) => updateField('email', e.target.value)}
                placeholder="info@example.com"
                data-testid="contact-email-input"
              />
            </div>

            <div className="pt-4 border-t">
              <h3 className="font-medium mb-4 text-foreground">Social Media</h3>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-foreground flex items-center gap-2">
                    <Instagram className="w-4 h-4" />
                    Instagram URL
                  </Label>
                  <Input
                    value={contactInfo.instagram}
                    onChange={(e) => updateField('instagram', e.target.value)}
                    placeholder="https://instagram.com/yourpage"
                    data-testid="contact-instagram-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-foreground flex items-center gap-2">
                    <Facebook className="w-4 h-4" />
                    Facebook URL
                  </Label>
                  <Input
                    value={contactInfo.facebook}
                    onChange={(e) => updateField('facebook', e.target.value)}
                    placeholder="https://facebook.com/yourpage"
                    data-testid="contact-facebook-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-foreground flex items-center gap-2">
                    <TikTokIcon className="w-4 h-4" />
                    TikTok URL
                  </Label>
                  <Input
                    value={contactInfo.tiktok || ''}
                    onChange={(e) => updateField('tiktok', e.target.value)}
                    placeholder="https://www.tiktok.com/@yourpage"
                    data-testid="contact-tiktok-input"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="mt-8 bg-secondary/30 rounded-2xl p-6 border border-border">
        <h3 className="font-heading text-lg text-foreground mb-4">Preview</h3>
        <p className="text-sm text-muted-foreground mb-4">
          This is how your contact information will appear on the Contact page:
        </p>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex gap-3 p-4 bg-card rounded-xl">
            <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center flex-shrink-0">
              <MapPin className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="font-medium text-sm text-foreground">Address</p>
              <p className="text-xs text-muted-foreground whitespace-pre-line">
                {contactInfo.address}
                {contactInfo.address_extra && `\n${contactInfo.address_extra}`}
              </p>
            </div>
          </div>

          <div className="flex gap-3 p-4 bg-card rounded-xl">
            <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center flex-shrink-0">
              <Phone className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="font-medium text-sm text-foreground">Phone</p>
              <p className="text-xs text-muted-foreground">{contactInfo.phone}</p>
            </div>
          </div>

          <div className="flex gap-3 p-4 bg-card rounded-xl">
            <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center flex-shrink-0">
              <Mail className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="font-medium text-sm text-foreground">Email</p>
              <p className="text-xs text-muted-foreground">{contactInfo.email}</p>
            </div>
          </div>

          <div className="flex gap-3 p-4 bg-card rounded-xl">
            <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center flex-shrink-0">
              <Clock className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="font-medium text-sm text-foreground">Hours</p>
              <p className="text-xs text-muted-foreground whitespace-pre-line">{contactInfo.hours}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
